import { useCallback, useEffect, useState } from 'react';
import styles from './Coupon.module.css';
import StatCard from '../../components/StatCard/StatCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import Modal, { ModalTitle } from '../../components/Modal/Modal';
import FilterSelect from '../../components/FilterSelect/FilterSelect';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import LimitCard from '../../components/LimitCard/LimitCard';
import { api, mapCoupon, mapSettings } from '../../lib/api';

const EMPTY_FORM = {
    code: '',
    description: '',
    rewardType: '% off points package price',
    value: '20',
    maxRedemption: '300',
    expiry: '',
    active: true,
};

// UI reward-type label → API rewardType value
const REWARD_PARAM = { '% off points package price': 'percent-off', 'Bonus points': 'bonus-points' };

const rewardTitle = (coupon) =>
    coupon.rewardType === 'Bonus points' ? `+${coupon.value} bonus points` : `${coupon.value}% off`;

const formatExpiry = (isoDate) => {
    if (!isoDate) return '--';
    const date = new Date(`${isoDate}T00:00:00`);
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${date.getDate()} ${month}, ${date.getFullYear()}`;
};

const Coupon = () => {
    const [coupons, setCoupons] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [form, setForm] = useState(EMPTY_FORM);
    const [createOpen, setCreateOpen] = useState(false);
    const [editCoupon, setEditCoupon] = useState(null);
    const [settings, setSettings] = useState(null);
    const [ceilingValue, setCeilingValue] = useState('0');
    const [ceilingOpen, setCeilingOpen] = useState(false);

    const loadCoupons = useCallback(async () => {
        try {
            const [{ coupons: list }, settingsRes] = await Promise.all([
                api('/admin/coupons'),
                api('/admin/settings'),
            ]);
            setCoupons(list.map(mapCoupon));
            setSettings(mapSettings(settingsRes.settings));
        } catch {
            setCoupons([]);
        }
    }, []);

    useEffect(() => {
        loadCoupons();
    }, [loadCoupons]);

    const activeCodes = coupons.filter((coupon) => coupon.status === 'Active').length;
    // The platform counter, not a sum over the table: a deleted coupon's
    // redemptions still happened and still count against the ceiling, but its
    // row is gone. Falls back to the table until settings have loaded.
    const totalRedemptions =
        settings?.coupons.totalRedemptions ?? coupons.reduce((sum, coupon) => sum + coupon.used, 0);

    const filteredCoupons = coupons.filter((coupon) => {
        const query = search.toLowerCase();
        const matchesSearch =
            coupon.code.toLowerCase().includes(query) ||
            coupon.description.toLowerCase().includes(query);
        const matchesStatus = statusFilter === 'All' || coupon.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const openCreateModal = () => {
        setForm(EMPTY_FORM);
        setCreateOpen(true);
    };

    const openEditModal = (coupon) => {
        setForm({
            code: coupon.code,
            description: coupon.description,
            rewardType: coupon.rewardType,
            value: String(coupon.value),
            maxRedemption: String(coupon.maxRedemption),
            expiry: coupon.expiry,
            active: coupon.status === 'Active',
        });
        setEditCoupon(coupon);
    };

    const formValid =
        form.code.trim() &&
        form.description.trim() &&
        Number(form.value) > 0 &&
        Number(form.maxRedemption) > 0 &&
        form.expiry;

    const buildBody = () => ({
        code: form.code.trim().toUpperCase(),
        description: form.description.trim(),
        rewardType: REWARD_PARAM[form.rewardType] || 'percent-off',
        value: Number(form.value),
        maxRedemption: Number(form.maxRedemption),
        status: form.active ? 'active' : 'inactive',
        expiry: form.expiry,
    });

    const handleCreate = async () => {
        try {
            await api('/admin/coupons', { method: 'POST', body: buildBody() });
            await loadCoupons();
        } catch (err) {
            alert(err.message);
        }
        setCreateOpen(false);
    };

    const handleEdit = async () => {
        try {
            await api(`/admin/coupons/${editCoupon._id}`, { method: 'PATCH', body: buildBody() });
            await loadCoupons();
        } catch (err) {
            alert(err.message);
        }
        setEditCoupon(null);
    };

    const handleToggleStatus = async (coupon) => {
        try {
            await api(`/admin/coupons/${coupon._id}`, {
                method: 'PATCH',
                body: { status: coupon.status === 'Active' ? 'inactive' : 'active' },
            });
            await loadCoupons();
        } catch (err) {
            alert(err.message);
        }
    };

    const openCeilingModal = () => {
        setCeilingValue(String(settings.coupons.maxTotalRedemptions));
        setCeilingOpen(true);
    };

    // 0 is a real value here (no ceiling), so this is a bounds check.
    const ceilingValid = Number.isInteger(Number(ceilingValue)) && Number(ceilingValue) >= 0;

    const handleSaveCeiling = async () => {
        try {
            await api('/admin/settings', {
                method: 'PATCH',
                body: { coupons: { maxTotalRedemptions: Number(ceilingValue) } },
            });
            await loadCoupons();
        } catch (err) {
            alert(err.message);
        }
        setCeilingOpen(false);
    };

    const renderCouponForm = (title, submitLabel, onSubmit, onClose) => (
        <Modal onClose={onClose}>
            <ModalTitle>{title}</ModalTitle>

            <label htmlFor="coupon-code" className={`${styles.inputLabel} mt-4 mb-2`}>Code</label>
            <input
                id="coupon-code"
                type="text"
                className={`${styles.input} w-100`}
                placeholder="E.g. FARM40"
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
            />

            <label htmlFor="coupon-description" className={`${styles.inputLabel} mt-4 mb-2`}>Description</label>
            <input
                id="coupon-description"
                type="text"
                className={`${styles.input} w-100`}
                placeholder="E.g. 20% off any points package"
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
            />

            <label htmlFor="coupon-reward-type" className={`${styles.inputLabel} mt-4 mb-2`}>Reward type</label>
            <select
                id="coupon-reward-type"
                className={`${styles.input} w-100`}
                value={form.rewardType}
                onChange={(event) => setForm({ ...form, rewardType: event.target.value })}
            >
                <option>% off points package price</option>
                <option>Bonus points</option>
            </select>

            <label htmlFor="coupon-value" className={`${styles.inputLabel} mt-4 mb-2`}>Value</label>
            <input
                id="coupon-value"
                type="number"
                min="1"
                className={`${styles.input} w-100`}
                value={form.value}
                onChange={(event) => setForm({ ...form, value: event.target.value })}
            />

            <label htmlFor="coupon-max" className={`${styles.inputLabel} mt-4 mb-2`}>Max redemption</label>
            <input
                id="coupon-max"
                type="number"
                min="1"
                className={`${styles.input} w-100`}
                value={form.maxRedemption}
                onChange={(event) => setForm({ ...form, maxRedemption: event.target.value })}
            />

            <label htmlFor="coupon-expiry" className={`${styles.inputLabel} mt-4 mb-2`}>Expiry date</label>
            <input
                id="coupon-expiry"
                type="date"
                className={`${styles.input} w-100`}
                value={form.expiry}
                onChange={(event) => setForm({ ...form, expiry: event.target.value })}
            />

            <label className={`${styles.checkboxRow} d-flex align-items-center gap-2 mt-4`}>
                <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(event) => setForm({ ...form, active: event.target.checked })}
                />
                Active
            </label>

            <Button className="w-100 mt-4" disabled={!formValid} onClick={onSubmit}>
                {submitLabel}
            </Button>
        </Modal>
    );

    return (
        <div>
            <div className="row g-3">
                <div className="col-12 col-md-6">
                    <StatCard value={String(activeCodes).padStart(2, '0')} label="Active codes" />
                </div>
                <div className="col-12 col-md-6">
                    {settings ? (
                        <LimitCard
                            title="Coupon redemptions"
                            description="Across every coupon code, platform-wide"
                            used={totalRedemptions}
                            limit={settings.coupons.maxTotalRedemptions}
                            usedNoun="redemptions"
                            actionLabel="Edit redemption limit"
                            onAction={openCeilingModal}
                            footnote="Applies on top of each code's own max redemption. Once this is reached, no coupon can be redeemed until it is raised."
                        />
                    ) : (
                        <StatCard value={String(totalRedemptions)} label="Total redemptions" />
                    )}
                </div>
            </div>

            <div className={`${styles.card} mt-4 p-4`}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h2 className={`${styles.cardTitle} mb-0`}>Coupon codes</h2>

                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <FilterSelect
                            label="Status"
                            value={statusFilter}
                            options={['All', 'Active', 'Inactive']}
                            onChange={setStatusFilter}
                        />
                        <SearchInput
                            placeholder="Search for coupons..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <Button className={styles.createButton} onClick={openCreateModal}>
                            Create coupon
                        </Button>
                    </div>
                </div>

                <div className={`${styles.tableWrap} table-responsive mt-3`}>
                    <table className={`${styles.table} w-100`}>
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Reward</th>
                                <th>Redemptions</th>
                                <th>Status</th>
                                <th>Expiry</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCoupons.map((coupon) => (
                                <tr key={coupon.id}>
                                    <td className={styles.code}>{coupon.code}</td>
                                    <td>
                                        <div className={styles.rewardTitle}>{rewardTitle(coupon)}</div>
                                        <div className={styles.rewardDescription}>{coupon.description}</div>
                                    </td>
                                    <td>{coupon.used}/{coupon.maxRedemption}</td>
                                    <td>
                                        <StatusBadge
                                            status={coupon.status}
                                            variant={coupon.status === 'Inactive' ? 'muted' : undefined}
                                        />
                                    </td>
                                    <td>{formatExpiry(coupon.expiry)}</td>
                                    <td className="text-end position-relative">
                                        <ActionMenu
                                            ariaLabel="Coupon actions"
                                            items={[
                                                { label: 'Edit coupon', onClick: () => openEditModal(coupon) },
                                                coupon.status === 'Active'
                                                    ? { label: 'Deactivate coupon', danger: true, onClick: () => handleToggleStatus(coupon) }
                                                    : { label: 'Activate coupon', onClick: () => handleToggleStatus(coupon) },
                                            ]}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {ceilingOpen && (
                <Modal onClose={() => setCeilingOpen(false)}>
                    <ModalTitle>Coupon redemption limit</ModalTitle>

                    <label htmlFor="coupon-ceiling" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Maximum redemptions across all coupons
                    </label>
                    <input
                        id="coupon-ceiling"
                        type="number"
                        min="0"
                        className={`${styles.input} w-100`}
                        value={ceilingValue}
                        onChange={(event) => setCeilingValue(event.target.value)}
                    />
                    <p className={`${styles.helpText} mt-2 mb-0`}>
                        {totalRedemptions.toLocaleString()} redeemed so far. Enter 0 for no limit. This
                        sits on top of each code's own max redemption — whichever runs out first stops
                        the redemption.
                    </p>

                    <Button className="w-100 mt-4" disabled={!ceilingValid} onClick={handleSaveCeiling}>
                        Save changes
                    </Button>
                </Modal>
            )}

            {createOpen && renderCouponForm('Create new coupon', 'Add coupon', handleCreate, () => setCreateOpen(false))}

            {editCoupon && renderCouponForm('Edit coupon', 'Save changes', handleEdit, () => setEditCoupon(null))}
        </div>
    );
};

export default Coupon;
